import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import DataTableDataRow from './DataTableDataRow';

import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import TableContainer from '../../../components/Table/TableContainer';
import {
  CASES_DATA_REQUEST_LIST_TABLE_LABELS,
  DATA_LIST_TABLE_LABELS,
} from '../../../constants/tableLabels';
import usePagination from '../../../hooks/usePagination';
import { ActiveSortField, DataRequestShortData, FilterTypes } from '../../../types';
import { getDataRequests } from '../../../api/requestsApi/dataApi';
import TableFilters from '../../../components/TableFilters/TableFilters';
import { downloadCsvorExcel } from '../../../helpers/downloadCsv';
import TableActions from '../../../components/Table/TableActions';
import { DATA_REQUESTS_STATUS_FILTER_OPTIONS } from '../../../components/TableFilters/constants';
import useSnackBar from '../../../hooks/useSnackBar';
import { handleSortTableData } from '../../../helpers/handleSortTableData';

const DataView = () => {
  const params = useParams();
  const firmId = localStorage.getItem('firmId');

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();

  const [dataList, setDataList] = useState<DataRequestShortData[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const [filters, setFilters] = useState<FilterTypes>({
    firmId: firmId || '',
    clientId: '',
    defendantId: '',
  });
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    plaintiff: {
      order: '',
      fieldName: 'plaintiff',
    },
    sentAt: {
      order: '',
      fieldName: 'sentAt',
    },
    completedAt: {
      order: '',
      fieldName: 'completedAt',
    },
  };

  const hasParams = !!Object.entries(params).length;

  useEffect(() => {
    getDataRequests({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setDataList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, activeSortField]);

  const exportList = () => {
    downloadCsvorExcel('/v5/data-requests/export', 'POST', 'Data_List','csv', {
      page: pageNumber,
      size: rowsPerPage,
      count: true,
      filters: { ...filters, ...(hasParams && params) },
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'Data requests exported successfully.',
          type: 'success',
        });
      }
    });
  };

  return (
    <>
      {isDataLoading && !dataList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={`/requests/create-data-request${
              params.caseId ? `/?caseId=${params.caseId}` : '/'
            }`}
            exportList={exportList}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              client
              timeFilter={'Request Date'}
              statusFilter={DATA_REQUESTS_STATUS_FILTER_OPTIONS}
              isStatusArray
            />
          )}

          {!isDataLoading && dataList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={
                params.caseId ? CASES_DATA_REQUEST_LIST_TABLE_LABELS : DATA_LIST_TABLE_LABELS
              }
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <DataTableDataRow tableData={dataList} />
            </TableContainer>
          )}
        </>
      )}
      <Outlet />
    </>
  );
};

export default DataView;
